import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "semantic-ui-react";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";
// ヘッダー・集計カード・タブ・ボタン・トーストは特別ナンバー画面のスタイルをそのまま使う。
import "../../../../styles/scss/recital2027SpecialWork.scss";
import "../../../../styles/scss/recital2027SeatAssign.scss";
import {
  OTHER_STUDIOS,
  PARTS,
  ROW_ORDER,
  SEAT_TYPES,
  autoAssignAll,
  autoAssignPart,
  autoZone,
  buildVenue,
  demandOf,
  famSeats,
  famStatus,
  isBoth,
  isSeatType,
  partsOf,
  placeAt,
  placeOne,
  releaseBlocked,
  sameSeats,
  seatLabel,
  studioOrder,
  toSavePayload,
  zoneSummary,
  zonesFromApi,
} from "./seatAssignLogic";

/**
 * 発表会2027（SLASH GALLERY vol.5）指定席の座席割当。会場は SkyシアターMBS（客席図 PDF の 1,289 席）。
 *
 * 割り振る家族: 1次の確定後は当選した家族に加え、2次の確定前の申込も「指定席の抽選申込」で決めている
 * 当選席数（当選予定）で含める（family.pending）。
 *
 * 1. 部ごとに席の役割を決める: 関係者席・生徒席・カメラ席を塗り、S/A/B の範囲は
 *    「希望数に合わせる」（空いている席だけで前の列から）か手で塗る。2階も自由に塗れる。
 * 2. 自動で割り振る: S/A/B の範囲の空席にだけ、スタジオでまとめて並び席で置く（スタジオの順は毎回ランダム）。
 *    範囲を前から 3 列ずつの帯に分け、帯の中はブロックごとに通路で折り返して埋める（スタジオが四角くまとまる）。
 *    空席は各席種の最後の列の両端にだけ残す。並びで入らない家族は 1 人席が出ないように分けて近くに置く。
 *    1次・2次の両方に当選した家族は席種ごとに並びでまとめ、両部に席がある家族は両部で同じ席を優先する。
 *    「席を離して置く」印の家族（イレギュラーの希望）は並びにせず、できるだけ離れた席に置く
 *    （計算は seatAssignLogic.js）。
 * 3. 手で直して保存する。当落の確定前も割当を保存でき、確定後もそのまま引き継ぐ
 *    （確定前は当選扱いの家族を申込席数で扱う）。落選・取り消しになった家族の席や、申込の席数を
 *    減らした家族の多い分の席は、読み込むときにサーバーが外して空席にする。
 * テーブルは slash-app が所有（apps.recital2027 0013）。API は views_recital_seat.py。
 */

const apiBase = process.env.REACT_APP_API_URL;

const ZONES = {
  S: { label: "S席", color: "#f0c75e" },
  A: { label: "A席", color: "#5ecbe0" },
  B: { label: "B席", color: "#ff8ab0" },
  staff: { label: "関係者席", color: "#b07cff" },
  student: { label: "生徒席", color: "#9fd84a" },
  camera: { label: "カメラ席", color: "#8a8fae" },
  none: { label: "未使用", color: "#3a3f66" },
};
const BRUSHES = ["S", "A", "B", "staff", "student", "camera", "none"];
const STUDIO_COLORS = [
  "#ff8a5c", "#5c9dff", "#5fd18b", "#d17be8", "#f2c14e", "#3fc9c0", "#ff6f9c", "#8f86ff",
  "#b7cf3f", "#e09a5a", "#6fb6d6", "#d28ab0", "#79c9a0", "#e5786d", "#a596e6", "#f0a83a",
  "#58b8e8", "#c9a07a", "#86c46a", "#e07fb8", "#7f9ee0", "#e6b25a",
];
const STATUS_LABELS = { applied: "当選扱い（確定前）", won: "当選・請求前", invoiced: "未払い", paid: "支払い済み" };
const FILTERS = [
  { value: "all", label: "すべて" },
  { value: "short", label: "未割当あり" },
  { value: "done", label: "割当済" },
  { value: "mismatch", label: "区分違い" },
  { value: "both", label: "両部" },
  { value: "diff", label: "両部で席が違う" },
  { value: "unpaid", label: "未払い" },
  { value: "apart", label: "席を離す" },
];
const STAGE = "M330 28 L574 28 L589 52 L315 52 Z";
const WHEELCHAIRS = [[211.6, 340], [232.5, 344.7], [251.5, 349.4], [625.2, 351.9], [644.5, 347.9], [664.5, 343.3], [684.1, 338]];
const VIEW_ALL = { x: 190, y: 18, w: 530, h: 1125 };
const VIEW_1F = { x: 195, y: 20, w: 520, h: 680 };

const other = (p) => (p === "1" ? "2" : "1");

function compactSeats(list) {
  const m = new Map();
  for (const x of list) {
    const k = `${x.s.f === 1 ? "1階" : "2階"} ${x.s.r}列`;
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(x.s.n);
  }
  return [...m].map(([k, ns]) => `${k} ${ns.sort((a, b) => a - b).join("・")}番`);
}

export default function SeatAssignPage() {
  useLoginCheck();

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  // 席の役割・割当は Map を直接書き換え、rev を進めて描き直す
  const [zones, setZones] = useState(null); // {1: Map, 2: Map}
  const [asgs, setAsgs] = useState(null); // {1: Map, 2: Map}
  const [versions, setVersions] = useState({ 1: 0, 2: 0 });
  const [dirty, setDirty] = useState({ 1: false, 2: false });
  const [rev, setRev] = useState(0);
  const bump = () => setRev((r) => r + 1);

  const [part, setPart] = useState("1");
  const [mode, setMode] = useState("assign"); // assign | zone
  const [colorBy, setColorBy] = useState("studio"); // studio | zone
  const [brush, setBrush] = useState("staff");
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const [flt, setFlt] = useState("all");

  const venue = useMemo(() => (data ? buildVenue(data.seats) : null), [data]);
  const families = useMemo(() => data?.families || [], [data]);
  const famById = useMemo(() => new Map(families.map((f) => [f.id, f])), [families]);
  const confirmed = !!data?.confirmed;

  const showToast = useCallback((text, kind = "info") => {
    setToast({ text, kind });
    window.clearTimeout(showToast.t);
    showToast.t = window.setTimeout(() => setToast(null), 3200);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/get_recital2027_seat_assign/`, { credentials: "include" });
      if (!res.ok) throw new Error(`取得に失敗しました (${res.status})`);
      const json = await res.json();
      const v = buildVenue(json.seats);
      const z = {};
      const a = {};
      const d = { 1: false, 2: false };
      const dropped = [];
      for (const p of PARTS) {
        const api = json.parts[p];
        z[p] = zonesFromApi(v, api.zones);
        // 一度も保存していない部は、希望数に合わせた範囲から始める（保存すると残る）
        if (!api.zones) {
          z[p] = autoZone(v, z[p], demandOf(json.families, p));
          d[p] = true;
        }
        a[p] = new Map(api.assignments.map(([sid, fid, t]) => [sid, { fid, t }]));
        // 外した席があれば、保存するとテーブルからも消えるので「保存していない変更」にする
        if (api.dropped) {
          dropped.push(`${p}部 ${api.dropped}席`);
          d[p] = true;
        }
      }
      setData(json);
      setZones(z);
      setAsgs(a);
      setVersions({ 1: json.parts["1"].version, 2: json.parts["2"].version });
      setDirty(d);
      setError("");
      bump();
      if (dropped.length) {
        showToast(`落選・取り消し・申込の席数の変更で、${dropped.join("・")}を外しました。保存すると反映されます`, "warn");
      }
    } catch (e) {
      setError(e.message || "取得に失敗しました");
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // 保存していない変更があるときはページを離れる前に確認する
  useEffect(() => {
    const anyDirty = dirty[1] || dirty[2];
    const onBefore = (e) => {
      if (!anyDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, [dirty]);

  const markDirty = (...ps) => setDirty((d) => ({ ...d, ...Object.fromEntries(ps.map((p) => [p, true])) }));

  // ---------- 集計 ----------
  const partFamilies = useMemo(() => families.filter((f) => partsOf(f).includes(part)), [families, part]);
  const studios = useMemo(() => studioOrder(families, part), [families, part]);
  const studioColor = useMemo(() => {
    const all = [...new Set(families.flatMap((f) => Object.values(f.studio)))].sort((a, b) => a.localeCompare(b, "ja"));
    const m = {};
    let i = 0;
    for (const s of all) m[s] = OTHER_STUDIOS.includes(s) ? "#9aa0c4" : STUDIO_COLORS[i++ % STUDIO_COLORS.length];
    return m;
  }, [families]);

  const stats = useMemo(() => {
    if (!venue || !zones) return null;
    const z = zones[part];
    const asg = asgs[part];
    const sum = zoneSummary(venue, z);
    const demand = demandOf(families, part);
    const used = { S: 0, A: 0, B: 0 };
    let floor2Used = 0;
    for (const sid of asg.keys()) {
      if (isSeatType(z.get(sid))) used[z.get(sid)] += 1;
      if (venue.byId.get(sid).f === 2) floor2Used += 1;
    }
    let shortSeats = 0;
    let shortFams = 0;
    for (const f of partFamilies) {
      const st = famStatus(venue, z, asg, f, part);
      shortSeats += st.short;
      if (st.short) shortFams += 1;
    }
    const both = families.filter(isBoth);
    const same = both.filter((f) => sameSeats(venue, asgs, f)).length;
    const free = SEAT_TYPES.reduce((n, t) => n + sum.count[t] - used[t], 0);
    return { sum, demand, used, floor2Used, shortSeats, shortFams, both: both.length, same, free };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue, zones, asgs, part, families, partFamilies, rev]);

  // ---------- 操作 ----------
  const paintSeats = (ids) => {
    const z = zones[part];
    let changed = false;
    for (const id of ids) {
      if (z.get(id) !== brush) {
        z.set(id, brush);
        changed = true;
      }
    }
    if (!changed) return;
    const released = releaseBlocked(z, asgs[part]);
    if (released.length) {
      showToast(`${released.map((id) => famById.get(id)?.name).join("、")} の席を外しました（未割当に戻りました）`);
    }
    markDirty(part);
    bump();
  };

  // Shift+クリックで、選んでいる家族の席を 1 席ずつ置く・外す（自動割当の後の手直し）
  const seatShiftClick = (s) => {
    const f = famById.get(sel);
    if (!partsOf(f).includes(part)) {
      showToast(`${f.name} さんは${part}部の席がありません`, "warn");
      return;
    }
    const r = placeOne(zones[part], asgs[part], f, part, s);
    if (!r.ok) {
      const z = zones[part].get(s.id);
      const msg = {
        blocked: `${ZONES[z]?.label}には家族を割り当てられません`,
        full: `${f.name} さんの${part}部の席はすべて置いてあります。動かすときは、先に動かす席を Shift+クリックで外してください`,
      }[r.reason];
      if (msg) showToast(msg, "warn");
      return;
    }
    if (r.action === "remove") showToast(`${seatLabel(s)} を外しました。Shift+クリックで別の席に 1 席置けます`);
    markDirty(part);
    bump();
  };

  const seatClick = (s, shift = false) => {
    if (mode !== "assign") return;
    const a = asgs[part].get(s.id);
    if (shift && sel && (!a || a.fid === sel)) {
      seatShiftClick(s);
      return;
    }
    if (a) {
      setSel((cur) => (cur === a.fid ? null : a.fid));
      return;
    }
    const z = zones[part].get(s.id);
    if (!isSeatType(z)) {
      showToast(`${ZONES[z].label}には家族を割り当てられません`, "warn");
      return;
    }
    if (!sel) {
      showToast("先に一覧か座席図で家族を選んでください", "warn");
      return;
    }
    const f = famById.get(sel);
    if (!partsOf(f).includes(part)) {
      showToast(`${f.name} さんは${part}部の席がありません`, "warn");
      return;
    }
    const r = placeAt(venue, zones[part], asgs[part], f, part, s);
    if (!r.ok) {
      showToast(`${s.r}列のこの位置には ${r.n} 席並びで空いていません`, "warn");
      return;
    }
    markDirty(part);
    bump();
  };

  const runZoneAuto = () => {
    zones[part] = autoZone(venue, zones[part], demandOf(families, part));
    releaseBlocked(zones[part], asgs[part]);
    markDirty(part);
    bump();
    showToast(`${part}部の S/A/B の範囲を希望数に合わせました。「両部とも自動で割り当て直す」で席に反映します`);
  };

  const copyZonesToOther = () => {
    const o = other(part);
    zones[o] = new Map(zones[part]);
    const released = releaseBlocked(zones[o], asgs[o]);
    markDirty(o);
    bump();
    showToast(`${part}部の席の役割を${o}部にコピーしました${released.length ? `（${o}部で ${released.length} 家族の席を外しました）` : ""}`);
  };

  const runAutoAll = () => {
    const next = autoAssignAll(venue, zones, families);
    setAsgs({ 1: next[1], 2: next[2] });
    markDirty("1", "2");
    bump();
  };

  const runAutoRest = () => {
    autoAssignPart(venue, zones, asgs, families, part);
    markDirty(part);
    bump();
  };

  const clearPart = () => {
    asgs[part].clear();
    setSel(null);
    markDirty(part);
    bump();
  };

  const unassignFamily = (fid) => {
    for (const [sid, a] of [...asgs[part]]) if (a.fid === fid) asgs[part].delete(sid);
    markDirty(part);
    bump();
  };

  // もう一方の部も、この部と同じ席にする。席種ごとに、もう一方の部の席数だけこの部の席の前から使う
  // （この部の席のほうが少ない席種は、足りない分が未割当で残る）
  const syncOther = (f) => {
    const o = other(part);
    const plan = [];
    const lacking = [];
    for (const t of SEAT_TYPES) {
      const need = f.counts[o]?.[t] || 0;
      if (!need) continue;
      const src = famSeats(venue, asgs[part], f.id).filter((y) => y.t === t).slice(0, need);
      if (src.length < need) lacking.push(`${ZONES[t].label}${need - src.length}席`);
      plan.push(...src);
    }
    const bad = plan.filter((y) => {
      const a = asgs[o].get(y.s.id);
      return !isSeatType(zones[o].get(y.s.id)) || (a && a.fid !== f.id);
    });
    if (bad.length) {
      showToast(`${o}部では ${compactSeats(bad).join("、")} が使えないか、ほかの家族が座っています`, "warn");
      return;
    }
    for (const [sid, a] of [...asgs[o]]) if (a.fid === f.id) asgs[o].delete(sid);
    for (const y of plan) asgs[o].set(y.s.id, { fid: f.id, t: y.t });
    markDirty(o);
    bump();
    showToast(
      lacking.length
        ? `${o}部も同じ席にしました（${part}部より多い ${lacking.join("・")} は未割当です）`
        : `${o}部も同じ席にしました`,
      lacking.length ? "warn" : "info",
    );
  };

  // 席を離して置く印（家族単位・すぐ DB に書く）。次の自動割り振りから効く
  const toggleApart = async (f) => {
    const apart = !f.apart;
    try {
      const res = await fetch(`${apiBase}/api/set_recital2027_seat_apart/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ family_key: f.family_key, apart }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `保存に失敗しました (${res.status})`);
      for (const x of families) if (x.family_key === f.family_key) x.apart = apart;
      bump();
      showToast(
        apart
          ? `${f.name} さんの席を離して置きます。自動で割り振ると、できるだけ離れた席に置きます`
          : `${f.name} さんの「席を離す」を外しました`,
      );
    } catch (e) {
      showToast(e.message, "bad");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const nextVersions = { ...versions };
      for (const p of PARTS) {
        if (!dirty[p]) continue;
        const payload = toSavePayload(venue, zones[p], asgs[p]);
        const res = await fetch(`${apiBase}/api/save_recital2027_seat_assign/`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            part: Number(p),
            version: versions[p],
            zones: payload.zones,
            assignments: payload.assignments,
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(`${p}部: ${json.error || `保存に失敗しました (${res.status})`}`);
        nextVersions[p] = json.version;
        setDirty((d) => ({ ...d, [p]: false }));
      }
      setVersions(nextVersions);
      showToast("保存しました");
    } catch (e) {
      showToast(e.message, "bad");
    } finally {
      setSaving(false);
    }
  };

  // ---------- 一覧 ----------
  const listGroups = useMemo(() => {
    if (!venue || !zones) return [];
    const z = zones[part];
    const asg = asgs[part];
    const kw = q.trim();
    const groups = new Map();
    for (const f of partFamilies) {
      const st = famStatus(venue, z, asg, f, part);
      const same = sameSeats(venue, asgs, f);
      const studio = f.studio[part];
      if (kw && !(f.name.includes(kw) || studio.includes(kw))) continue;
      if (flt === "short" && !st.short) continue;
      if (flt === "done" && st.short) continue;
      if (flt === "mismatch" && !st.mis) continue;
      if (flt === "both" && !isBoth(f)) continue;
      if (flt === "diff" && same !== false) continue;
      if (flt === "unpaid" && f.status !== "invoiced") continue;
      if (flt === "apart" && !f.apart) continue;
      if (!groups.has(studio)) groups.set(studio, []);
      groups.get(studio).push({ f, st, same });
    }
    // 同じ家族（1次・2次の 2 行）が並ぶように、家族キー → 次の順にする
    const byFamily = (a, b) =>
      (a.f.family_key || "").localeCompare(b.f.family_key || "") || (a.f.round || 1) - (b.f.round || 1);
    return studios.filter((s) => groups.has(s)).map((s) => [s, groups.get(s).sort(byFamily)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue, zones, asgs, part, partFamilies, studios, q, flt, rev]);

  // ---------- 座席図 ----------
  const svgRef = useRef(null);
  const [vb, setVb] = useState(VIEW_1F);
  const drag = useRef(null);
  const painting = useRef(false);
  const [tip, setTip] = useState(null);

  const fit = useCallback((box) => {
    const el = svgRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const ar = r.width / r.height || 0.6;
    if (box.w / box.h > ar) setVb({ x: box.x, w: box.w, h: box.w / ar, y: box.y - (box.w / ar - box.h) / 2 });
    else setVb({ y: box.y, h: box.h, w: box.h * ar, x: box.x - (box.h * ar - box.w) / 2 });
  }, []);

  useEffect(() => {
    if (venue) fit(VIEW_1F);
  }, [venue, fit]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const k = Math.exp(e.deltaY * 0.0015);
      setVb((v) => {
        const px = v.x + ((e.clientX - r.left) / r.width) * v.w;
        const py = v.y + ((e.clientY - r.top) / r.height) * v.h;
        return { x: px - (px - v.x) * k, y: py - (py - v.y) * k, w: v.w * k, h: v.h * k };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [venue]);

  const zoomCenter = (k) =>
    setVb((v) => ({ x: v.x + (v.w * (1 - k)) / 2, y: v.y + (v.h * (1 - k)) / 2, w: v.w * k, h: v.h * k }));

  const onPointerDown = (e) => {
    const seatId = e.target.closest?.("[data-seat]")?.dataset.seat;
    if (mode === "zone" && seatId) {
      painting.current = true;
      paintSeats([Number(seatId)]);
      return;
    }
    drag.current = { x: e.clientX, y: e.clientY, vb, moved: false };
  };
  const onPointerMove = (e) => {
    if (painting.current) {
      const t = document.elementFromPoint(e.clientX, e.clientY);
      const sid = t?.closest?.("[data-seat]")?.dataset.seat;
      if (sid) paintSeats([Number(sid)]);
      return;
    }
    if (drag.current) {
      const dx = e.clientX - drag.current.x;
      const dy = e.clientY - drag.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) drag.current.moved = true;
      if (drag.current.moved) {
        const r = svgRef.current.getBoundingClientRect();
        const v0 = drag.current.vb;
        setVb({ ...v0, x: v0.x - (dx / r.width) * v0.w, y: v0.y - (dy / r.height) * v0.h });
        setTip(null);
      }
      return;
    }
    const sid = e.target.closest?.("[data-seat]")?.dataset.seat;
    if (!sid) {
      setTip(null);
      return;
    }
    const box = svgRef.current.parentElement.getBoundingClientRect();
    setTip({ id: Number(sid), x: e.clientX - box.left, y: e.clientY - box.top, w: box.width, h: box.height });
  };
  const onPointerUp = (e) => {
    if (painting.current) {
      painting.current = false;
      return;
    }
    if (!drag.current) return;
    const moved = drag.current.moved;
    drag.current = null;
    if (moved) return;
    const label = e.target.closest?.("[data-row]")?.dataset.row;
    if (label && mode === "zone") {
      paintSeats(venue.seats.filter((s) => s.r === label).map((s) => s.id));
      return;
    }
    const sid = e.target.closest?.("[data-seat]")?.dataset.seat;
    if (sid) seatClick(venue.byId.get(Number(sid)), e.shiftKey);
    else if (mode === "assign") setSel(null);
  };

  const rowLabels = useMemo(() => {
    if (!venue) return [];
    return ROW_ORDER.map((r) => {
      const c = venue.blocks.get(`${r}|C`);
      if (!c) return null;
      return { r, l: [c[0].x - 15, c[0].y], rt: [c[c.length - 1].x + 15, c[c.length - 1].y] };
    }).filter(Boolean);
  }, [venue]);

  const seatFill = (s) => {
    const z = zones[part].get(s.id);
    const a = asgs[part].get(s.id);
    if (mode === "zone") return { fill: z === "camera" ? "url(#sa-hatch)" : ZONES[z].color, stroke: ZONES[z].color, op: 1, num: "#0a0c1a" };
    if (z === "camera") return { fill: "url(#sa-hatch)", stroke: ZONES.camera.color, op: 0.6, num: "transparent" };
    if (z === "none") return { fill: "transparent", stroke: ZONES.none.color, op: 0.7, num: "#5a6090" };
    if (z === "staff" || z === "student") return { fill: ZONES[z].color, stroke: ZONES[z].color, op: 0.35, num: "#0a0c1a" };
    if (a) {
      const f = famById.get(a.fid);
      const fill = colorBy === "studio" ? studioColor[f?.studio[part]] || "#9aa0c4" : ZONES[a.t].color;
      const dim = sel && a.fid !== sel ? 0.25 : 1;
      return { fill, stroke: sel && a.fid === sel ? "#ffffff" : fill, op: dim, num: "#0a0c1a", sw: sel && a.fid === sel ? 1.6 : 0.7 };
    }
    return { fill: "transparent", stroke: ZONES[z].color, op: 1, num: "#c9cdea" };
  };

  // ---------- 描画 ----------
  if (error && !data) return <div className="recital2027-special-page"><div className="alert">{error}</div></div>;
  if (!venue || !zones || !stats) return <div className="recital2027-special-page"><div className="loading">読み込み中…</div></div>;

  const selFam = sel ? famById.get(sel) : null;
  const tipSeat = tip ? venue.byId.get(tip.id) : null;
  const anyDirty = dirty[1] || dirty[2];

  return (
    <div className="recital2027-special-page recital2027-seat-assign-page">
      <div className="page-header">
        <div>
          <p className="eyebrow">SLASH GALLERY vol.5</p>
          <h1>座席割当</h1>
          <p className="header-sub">
            SkyシアターMBS（1階 871席・2階 418席）。部ごとに関係者席・生徒席・カメラ席と S/A/B の範囲を決め、空いている席に家族を割り振ります。
          </p>
        </div>
        <div className="sa-head-actions">
          <button type="button" className="ghost-btn" onClick={load} disabled={saving}>
            <Icon name="refresh" /> 読み込み直す
          </button>
          <button type="button" className="primary-btn" onClick={save} disabled={saving || !anyDirty}>
            {saving ? "保存中…" : anyDirty ? `保存する（${PARTS.filter((p) => dirty[p]).map((p) => `${p}部`).join("・")}）` : "保存済み"}
          </button>
        </div>
      </div>

      {confirmed && families.some((f) => f.pending) && (
        <div className="sa-sim">
          <Icon name="info circle" />
          <div>
            <b>2次は当落の確定前です（{families.filter((f) => f.pending).length}件）。</b>
            「指定席の抽選申込」の 2次で決めている当選席数（変えていなければ、優先の部は申込どおり）で割り振ります。
            申込期限内は申込が増えたり変わったりするので、当選席数を変えたら「読み込み直す」を押し、期限後にもう一度割り当て直してください。
          </div>
        </div>
      )}

      {!confirmed && (
        <div className="sa-sim">
          <Icon name="info circle" />
          <div>
            <b>当落の確定前です。</b>
            「指定席の抽選申込」でチェックが付いている家族（当選扱い）を申込席数で割り振ります。割当は保存でき、確定後もそのまま使えます。
            落選にした家族の席は空席になります。当落のチェックを変えたら「読み込み直す」を押してください。
          </div>
        </div>
      )}

      <div className="sa-toolbar">
        <div className="tabs" role="tablist">
          {PARTS.map((p) => (
            <button key={p} type="button" className="tab" role="tab" aria-selected={part === p} onClick={() => setPart(p)}>
              {p}部<span className="c">{families.filter((f) => partsOf(f).includes(p)).length}家族</span>
              {dirty[p] && <span className="sa-dot" title="保存していない変更があります" />}
            </button>
          ))}
        </div>
        <div className="sa-seg" role="group" aria-label="モード">
          <button type="button" aria-pressed={mode === "assign"} onClick={() => setMode("assign")}>家族を割り振る</button>
          <button type="button" aria-pressed={mode === "zone"} onClick={() => setMode("zone")}>席の役割を決める</button>
        </div>
        <div className="sa-seg" role="group" aria-label="色分け">
          <button type="button" aria-pressed={colorBy === "studio"} onClick={() => setColorBy("studio")}>スタジオ色</button>
          <button type="button" aria-pressed={colorBy === "zone"} onClick={() => setColorBy("zone")}>席種色</button>
        </div>
        <span className="spacer" />
        {mode === "zone" ? (
          <>
            <button type="button" className="ghost-btn" onClick={runZoneAuto}>S/A/B を希望数に合わせる</button>
            <button type="button" className="ghost-btn" onClick={copyZonesToOther}>{part}部の役割を{other(part)}部にコピー</button>
          </>
        ) : (
          <>
            <button type="button" className="ghost-btn" onClick={runAutoRest}>未割当だけ自動で割り振る</button>
            <button type="button" className="ghost-btn" onClick={runAutoAll}>両部とも自動で割り当て直す</button>
            <button type="button" className="danger-btn" onClick={clearPart}>{part}部の割当を全部外す</button>
          </>
        )}
      </div>

      <div className="kpis">
        <div className={`kpi ${stats.shortSeats ? "bad" : ""}`}>
          <span className="k">{part}部の席数（{confirmed ? "当選" : "申込"}）</span>
          <span className="v">
            {stats.demand.S + stats.demand.A + stats.demand.B}
            <small>席・{partFamilies.length}家族</small>
          </span>
          <span className="sub">
            {stats.shortSeats ? `未割当 ${stats.shortSeats}席（${stats.shortFams}家族）` : "全員割り振り済み"}
          </span>
        </div>
        {SEAT_TYPES.map((t) => {
          const over = stats.demand[t] - stats.sum.count[t];
          return (
            <div key={t} className={`kpi ${over > 0 ? "warn" : ""}`}>
              <span className="k"><i className="sa-sw" style={{ background: ZONES[t].color }} />{ZONES[t].label} {stats.sum.range[t]}</span>
              <span className="v">{stats.used[t]}<small>/ {stats.sum.count[t]}席</small></span>
              <span className="sub">希望 {stats.demand[t]}席{over > 0 ? `・${over}席足りない` : ""}</span>
            </div>
          );
        })}
        <div className="kpi">
          <span className="k">関係者席・生徒席・カメラ席</span>
          <span className="v small">{stats.sum.count.staff}<small>・</small>{stats.sum.count.student}<small>・</small>{stats.sum.count.camera}</span>
          <span className="sub">未使用 {stats.sum.count.none}席・2階に置いた席 {stats.floor2Used}</span>
        </div>
        <div className={`kpi ${stats.same < stats.both ? "warn" : ""}`}>
          <span className="k">両部の家族</span>
          <span className="v">{stats.same}<small>/ {stats.both}家族が同じ席</small></span>
          <span className="sub">S/A/Bに残る空席 {stats.free}席</span>
        </div>
      </div>

      <div className="sa-main">
        <div className="sa-map">
          <svg
            ref={svgRef}
            viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={() => {
              setTip(null);
              painting.current = false;
              drag.current = null;
            }}
          >
            <defs>
              <pattern id="sa-hatch" width="3" height="3" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="3" height="3" fill="#161a36" />
                <line x1="0" y1="0" x2="0" y2="3" stroke="#8a8fae" strokeWidth="1.2" />
              </pattern>
            </defs>
            <path d={STAGE} fill="#2a2f55" />
            <text x="452" y="43" className="sa-caption">舞 台</text>
            <text x="452" y="352" className="sa-caption">客席1階</text>
            <text x="452" y="830" className="sa-caption">客席2階</text>
            {WHEELCHAIRS.map(([x, y]) => (
              <g key={`${x}`}>
                <rect x={x} y={y} width="16" height="18" rx="2" fill="none" stroke="#3a3f66" strokeWidth=".7" />
                <text x={x + 8} y={y + 9} className="sa-wc">車椅子</text>
              </g>
            ))}
            {venue.seats.map((s) => {
              const c = seatFill(s);
              return (
                <g key={s.id} data-seat={s.id} transform={`translate(${s.x} ${s.y}) rotate(${s.a})`} className="sa-seat" opacity={c.op}>
                  <rect x="-4.7" y="-4.7" width="9.4" height="9.4" rx="1.8" fill={c.fill} stroke={c.stroke} strokeWidth={c.sw || 0.7} />
                  <text y=".3" fill={c.num}>{s.n}</text>
                </g>
              );
            })}
            {rowLabels.map(({ r, l, rt }) => (
              <g key={r} data-row={r} className={`sa-rowlab ${mode === "zone" ? "edit" : ""}`}>
                <text x={l[0]} y={l[1]}>{r}</text>
                <text x={rt[0]} y={rt[1]}>{r}</text>
              </g>
            ))}
          </svg>

          {mode === "zone" && (
            <div className="sa-brush">
              <span className="dim">塗る役割</span>
              {BRUSHES.map((k) => (
                <button key={k} type="button" aria-pressed={brush === k} onClick={() => setBrush(k)}>
                  <i className="sa-sw" style={{ background: ZONES[k].color }} />
                  {ZONES[k].label}
                </button>
              ))}
              <span className="dim">席をクリック／ドラッグ、列の記号で 1 列まとめて</span>
            </div>
          )}
          <div className="sa-mapctl">
            <button type="button" onClick={() => zoomCenter(0.7)} aria-label="拡大">＋</button>
            <button type="button" onClick={() => zoomCenter(1 / 0.7)} aria-label="縮小">－</button>
            <button type="button" onClick={() => fit(VIEW_1F)}>1F</button>
            <button type="button" onClick={() => fit(VIEW_ALL)}>全体</button>
          </div>
          <div className="sa-legend">
            {mode === "zone" || colorBy === "zone"
              ? BRUSHES.map((k) => (
                  <span key={k}><i className="sa-sw" style={{ background: ZONES[k].color }} />{ZONES[k].label}</span>
                ))
              : studios.map((s) => (
                  <span key={s}><i className="sa-sw" style={{ background: studioColor[s] }} />{s.split("・")[1] || s}</span>
                ))}
          </div>
          {tipSeat && (
            <div
              className="sa-tip"
              style={{
                left: tip.x + 190 > tip.w ? tip.x - 190 : tip.x + 14,
                top: tip.y + 90 > tip.h ? tip.y - 96 : tip.y + 14,
              }}
            >
              <b className="mono">{seatLabel(tipSeat)}</b>
              <div className="dim">{ZONES[zones[part].get(tipSeat.id)].label}</div>
              {(() => {
                const a = asgs[part].get(tipSeat.id);
                if (!a) return null;
                const f = famById.get(a.fid);
                return (
                  <div>
                    {f?.name}
                    <div className="dim">{f?.studio[part]}・{ZONES[a.t].label}として</div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        <aside className="sa-side">
          <div className="sa-card">
            {selFam ? (
              <FamilyDetail
                f={selFam}
                part={part}
                venue={venue}
                zones={zones}
                asgs={asgs}
                studioColor={studioColor}
                onUnassign={() => unassignFamily(selFam.id)}
                onSync={() => syncOther(selFam)}
                onToggleApart={() => toggleApart(selFam)}
                onClose={() => setSel(null)}
              />
            ) : (
              <div className="sa-help">
                <b>使い方</b>
                <ol>
                  <li>「席の役割を決める」で関係者席・生徒席・カメラ席を塗り、S/A/B の範囲を決める</li>
                  <li>「家族を割り振る」で「両部とも自動で割り当て直す」を押す（前から 3 列ずつ、通路で折り返してスタジオを四角くまとめる。並びで入らない家族は分けて近くに置く）</li>
                  <li>一覧か座席図で家族を選び、空いている席をクリックすると並びで配置・移動</li>
                  <li>席を離して置きたい家族は、選んで「席を離して置く」を押してから自動で割り振る（できるだけ離れた席に置く）</li>
                  <li>Shift を押しながらクリックで 1 席ずつ置く・外す</li>
                  <li>最後に「保存する」</li>
                </ol>
              </div>
            )}
          </div>
          <div className="sa-card">
            <div className="sa-filters">
              <input id="sa-q" type="search" placeholder="名前・スタジオで検索" value={q} onChange={(e) => setQ(e.target.value)} />
              <select id="sa-flt" value={flt} onChange={(e) => setFlt(e.target.value)} aria-label="絞り込み">
                {FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="sa-list">
              {listGroups.length === 0 && <p className="dim sa-empty">該当する家族はいません</p>}
              {listGroups.map(([studio, rows]) => (
                <div key={studio}>
                  <div className="sa-grp">
                    <i className="sa-sw" style={{ background: studioColor[studio] }} />
                    {studio}
                    <small>{rows.length}家族</small>
                  </div>
                  {rows.map(({ f, st, same }) => (
                    <button
                      key={f.id}
                      type="button"
                      className="sa-fam"
                      aria-current={f.id === sel}
                      onClick={() => setSel(f.id === sel ? null : f.id)}
                    >
                      <span className="nm">
                        {f.name}
                        {f.round === 2 && <span className="sa-both">{f.pending ? "2次・確定前" : "2次"}</span>}
                        {isBoth(f) && <span className="sa-both">両部</span>}
                        {f.apart && <span className="sa-both">席を離す</span>}
                        {f.status === "invoiced" && <span className="sa-unpaid">{f.overdue ? "期限切れ" : "未払い"}</span>}
                      </span>
                      {st.short ? (
                        <span className="pill bad">不足{st.short}</span>
                      ) : same === false ? (
                        <span className="pill warn">席ズレ</span>
                      ) : st.mis ? (
                        <span className="pill warn">区分違い</span>
                      ) : (
                        <span className="pill ok">済</span>
                      )}
                      <span className="rq mono">
                        {SEAT_TYPES.filter((t) => f.counts[part][t]).map((t) => `${t}${f.counts[part][t]}`).join(" ")}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {toast && <div className={`toast sa-toast ${toast.kind}`}>{toast.text}</div>}
    </div>
  );
}

function FamilyDetail({ f, part, venue, zones, asgs, studioColor, onUnassign, onSync, onToggleApart, onClose }) {
  const ss = famSeats(venue, asgs[part], f.id);
  const o = other(part);
  const os = famSeats(venue, asgs[o], f.id);
  const same = sameSeats(venue, asgs, f);
  const mis = ss.filter((y) => zones[part].get(y.s.id) !== y.t);
  const inPart = partsOf(f).includes(part);
  return (
    <div className="sa-detail">
      <div className="sa-detail-name">
        {f.name}
        <span className="sa-chip">
          <i className="sa-sw" style={{ background: studioColor[f.studio[part]] }} />
          {f.studio[part]}
        </span>
        {f.round === 2 && <span className="sa-both">2次</span>}
        {isBoth(f) && <span className="sa-both">両部</span>}
        {f.apart && <span className="sa-both">席を離す</span>}
      </div>
      <div className="dim">{f.pending ? "2次の当選予定（確定前）" : STATUS_LABELS[f.status] || f.status}{f.overdue ? "（期限切れ）" : ""}</div>
      {!inPart && <div className="dim">{part}部の席はありません</div>}
      {inPart && (
        <div className="sa-req">
          {SEAT_TYPES.filter((t) => f.counts[part][t]).map((t) => {
            const have = ss.filter((y) => y.t === t).length;
            return (
              <React.Fragment key={t}>
                <span className="sa-chip"><i className="sa-sw" style={{ background: ZONES[t].color }} />{ZONES[t].label}</span>
                <span className="mono">{have} / {f.counts[part][t]}席</span>
                {have < f.counts[part][t] ? <span className="pill bad">不足{f.counts[part][t] - have}</span> : <span className="pill ok">OK</span>}
              </React.Fragment>
            );
          })}
        </div>
      )}
      <div>
        <div className="dim">{part}部の座席</div>
        <div className="mono sa-seats">{ss.length ? compactSeats(ss).join(" / ") : "まだありません。空いている席をクリックして配置します"}</div>
        <div className="dim">Shift+クリックで 1 席ずつ置く・外す</div>
      </div>
      {mis.length > 0 && <span className="pill warn">{mis.length}席が希望と違う席種の範囲に入っています</span>}
      {isBoth(f) && (
        <div>
          <div className="dim">
            {o}部の座席{" "}
            {same === true && <span className="pill ok">同じ席</span>}
            {same === false && <span className="pill warn">席が違う</span>}
          </div>
          <div className="mono sa-seats">{os.length ? compactSeats(os).join(" / ") : "未割当"}</div>
        </div>
      )}
      <div className="sa-detail-actions">
        <button type="button" className="danger-btn" onClick={onUnassign} disabled={!ss.length}>{part}部の席を外す</button>
        {isBoth(f) && ss.length > 0 && <button type="button" className="ghost-btn" onClick={onSync}>{o}部も同じ席にする</button>}
        <button type="button" className="ghost-btn" onClick={onToggleApart}>
          {f.apart ? "「席を離す」を外す" : "席を離して置く"}
        </button>
        <button type="button" className="ghost-btn" onClick={onClose}>選択を解除</button>
      </div>
    </div>
  );
}
